// Palworld OFFICIAL CARD GAME booster box reprint news cluster.
//
// Covers the Dawn of Palpagos (EBP01) supply shortage and the confirmed
// additional print runs, plus what a reprint does to sealed box and single
// card pricing. Restock/price mechanics live here; set contents live in the
// product guides and rules live in guides-tcg-rules.ts.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

const QUESTION = "Is the Palworld TCG booster box getting a reprint?";
const ANSWER =
  "Yes, and it now comes in two waves. Bushiroad is shipping extra 1st Edition stock of Booster Box \"Dawn of Palpagos\" (EBP01) through September 2026, then a separately marked 2nd Edition print run in late October 2026. The set launched on 30 July 2026 and passed 4.4 million packs sold worldwide by 8 September 2026, and the publisher apologised twice for the shortage before putting dates on the reprint. The card list and the printed English card codes are unchanged, so nothing changes for deck legality — only availability, edition marking and price.";

const STEPS: [string, string][] = [
  ["Confirm the product you are buying", "The reprint covers the EBP01 sealed booster box (12 packs, 7 cards per pack). Trial decks ETD01 and ETD02 are separate products with their own restock timing."],
  ["Check the live sealed price before you order", "Sealed box asks spiked while the set was unavailable. Compare the current asking price against recent sold data on the price chart before paying a shortage premium."],
  ["Wait out the first restock spike", "Restock waves usually pull sealed prices down over the following weeks. If you are buying to play rather than to hold, waiting is almost always cheaper."],
  ["Buy singles for playables", "If you need specific cards for a deck, singles stay cheaper than chasing them through sealed product, and reprint pressure hits chase singles first."],
  ["Track the cards you actually need", "Put the exact printed codes on your want list, then let the price index tell you when the reprint has softened those cards."],
];

const DATA: [string, string][] = [
  ["Set", "Booster Box \"Dawn of Palpagos\" — EBP01, the first Palworld Official Card Game set."],
  ["Launch date", "30 July 2026, worldwide, alongside trial decks ETD01 and ETD02."],
  ["Reported demand", "4.4 million packs sold worldwide as of 8 September 2026, up from 3.5 million in July."],
  ["Reprint timing", "Extra 1st Edition stock ships through September 2026; a marked 2nd Edition print run follows in late October 2026."],
  ["Edition marking", "The 2nd Edition run is identified on the packaging, so 1st Edition boxes stay distinguishable to collectors even though the cards inside play identically."],
  ["Publisher statement", "A shortage apology was posted on 12 August 2026, repeated on 19 August, then dated reprint details followed in early September 2026."],
  ["Box contents", "12 packs per box, 7 cards per pack — 84 cards per box."],
  ["Card codes", "Unchanged. Reprinted cards keep the same printed English codes such as EBP01-001."],
  ["Accessories", "Sleeve and card-set accessory releases were pushed back while supply catches up."],
];

const PITFALLS: [string, string][] = [
  ["Paying scalper prices during the gap", "Shortage asks are not sold prices. Check sold data before agreeing to a listing priced off the sold-out period."],
  ["Assuming reprints are a different card", "There is no separate reprint code or stamp announced for EBP01. A reprinted card is the same card for play and for pricing."],
  ["Holding sealed boxes expecting scarcity", "Confirmed additional print runs remove the scarcity thesis for sealed EBP01. Value now comes from chase singles, not sealed boxes."],
  ["Buying boxes to hunt one card", "A box averages far more than the single you want. Price the single first, then decide."],
];

const FAQ: [string, string][] = [
  ["Is Palworld TCG Dawn of Palpagos being reprinted?", "Yes. Bushiroad confirmed additional print runs of Booster Box \"Dawn of Palpagos\" (EBP01) after the launch stock sold out worldwide and the publisher apologised for the shortage."],
  ["Will reprinted Palworld cards look different?", "No difference has been announced. Reprinted cards keep the same artwork, rarity and printed English card codes, so they are identical for deck legality and for price tracking."],
  ["Will the reprint lower Palworld TCG card prices?", "Usually yes for sealed boxes and mid-rarity singles, because supply rises against the same demand. Top chase rarities hold their value best, since a reprint adds copies at the same low pull rate."],
  ["Should I buy a booster box now or wait for the reprint?", "If you want cards to play with, wait — restock waves normally push sealed prices back toward retail. If you want a specific card, buy the single instead of the box."],
  ["How many cards are in a Palworld booster box?", "A Dawn of Palpagos booster box holds 12 packs of 7 cards, so 84 cards per box."],
  ["Why did the Palworld card game sell out?", "Demand far exceeded the launch print run: the set passed 4.4 million packs sold by 8 September 2026 after its 30 July release, and resellers absorbed much of the remaining retail stock."],
  ["When is the Palworld TCG 2nd Edition coming out?", "Late October 2026. Extra 1st Edition boxes ship through September 2026 first, then the marked 2nd Edition print run arrives at the end of October."],
  ["Is 1st Edition Dawn of Palpagos worth more than 2nd Edition?", "The 2nd Edition run is marked on the packaging, so 1st Edition sealed boxes keep a collector premium. For play and for singles pricing the two runs are the same cards with the same codes."],
  ["How many Palworld TCG packs have been sold?", "Bushiroad reported 4.4 million packs sold worldwide for Dawn of Palpagos on 8 September 2026, after reporting 3.5 million in July 2026."],
  ["Are the Palworld TCG accessories delayed too?", "Yes. Sleeve and card-set accessory releases were pushed back while the publisher prioritises restocking the core booster and trial deck products."],
];

const HUB = "/prices";
const HUB_LABEL = "Palworld TCG price chart";
const TITLE = "Palworld TCG Booster Box Reprint News";

function articles(): GuideArticle[] {
  const hubSentence = `Keep the ${HUB_LABEL} open at ${HUB} while you read this, so every price you act on is the live one rather than a shortage-period ask.`;
  return [
    {
      title: TITLE,
      slug: "overview",
      intro: `${ANSWER} ${hubSentence}`,
      sections: [
        { heading: QUESTION, text: ANSWER },
        { heading: "Why the reprint happened", text: "The first Palworld Official Card Game set launched into demand its print run could not meet. Retail sold out within days, resellers listed sealed product at multiples of retail, and the publisher published two shortage apologies before putting dates on the reprint: extra 1st Edition boxes through September 2026, then a marked 2nd Edition print run in late October 2026. Accessory releases slipped while production capacity went to the core products." },
        { heading: "What the reprint changes", text: DATA.map(([k, v]) => `${k}: ${v}`).join(" ") },
        { heading: `Use it with the ${HUB_LABEL}`, text: `${hubSentence} Compare sold data to the current ask before buying sealed product during a restock wave.` },
      ],
      externalLinks: pool(0),
    },
    {
      title: `${TITLE}: what to do now`,
      slug: "step-by-step",
      intro: "The order to follow so a reprint wave saves you money instead of costing it.",
      sections: STEPS.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(1),
    },
    {
      title: `${TITLE}: reference table`,
      slug: "reference",
      intro: "The confirmed facts behind the Dawn of Palpagos reprint, in the order you will need them.",
      sections: DATA.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(2),
    },
    {
      title: `${TITLE}: mistakes and fixes`,
      slug: "mistakes",
      intro: "Four ways buyers lose money around a reprint announcement, each with the fix.",
      sections: PITFALLS.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(3),
    },
    {
      title: `${TITLE}: FAQ`,
      slug: "faq",
      intro: "Direct answers to the questions players are asking about the Palworld booster box reprint.",
      sections: FAQ.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(1),
    },
  ];
}

export const REPRINT_NEWS_GUIDES: Guide[] = [
  {
    slug: "palworld-tcg-booster-box-reprint-news",
    title: "Palworld TCG Booster Box Reprint & Restock News (2026)",
    desc: "Official Palworld card game booster box restock status. Real-time updates on Dawn of Palpagos 2nd edition print runs and stock availability.",
    tag: "TCG News",
    updated: "2026-09-12",
    keywords:
      "palworld tcg booster box reprint, palworld card game reprint news, dawn of palpagos reprint, palworld tcg restock, palworld booster box sold out, palworld tcg second print run, palworld tcg 2nd edition, dawn of palpagos 2nd edition, palworld tcg 1st edition vs 2nd edition, palworld tcg 4.4 million packs",
    articles: articles(),
  },
];
